import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketsTeamDrawerService } from '../../../services/tickets-config-drawer.services';
import { TicketTeam } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-teams-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, HasPermissionDirective],
  templateUrl: './teams-list.component.html'
})
export class TicketsTeamsListComponent implements OnInit, OnDestroy {
  items: TicketTeam[] = [];
  filtered: TicketTeam[] = [];
  loading = false;
  error = '';

  filtroNombre = '';
  filtroSoloActivos = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    protected router: Router,
    private configApi: TicketsConfigApiService,
    private drawer: TicketsTeamDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargar();
    this.drawer.entitySaved$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.configApi.getTeams().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.items = data.sort((a, b) => a.name.localeCompare(b.name));
        this.aplicarFiltros();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar equipos';
        this.loading = false;
      }
    });
  }

  aplicarFiltros(): void {
    const term = this.filtroNombre.trim().toLowerCase();
    this.filtered = this.items.filter((t) => {
      if (term && !t.name.toLowerCase().includes(term)) return false;
      if (this.filtroSoloActivos && !t.isActive) return false;
      return true;
    });
  }

  nuevo(): void { this.drawer.open(); }
  editar(item: TicketTeam): void { this.drawer.open({ id: item.id }); }
  verMiembros(item: TicketTeam): void {
    this.router.navigate(['/tickets/config/teams', item.id]);
  }

  toggleActivo(item: TicketTeam): void {
    this.configApi.updateTeam(item.id, {
      name: item.name,
      description: item.description ?? undefined,
      isActive: !item.isActive
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.cargar(),
      error: (err) => this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo actualizar el equipo' })
    });
  }

  async eliminar(item: TicketTeam): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar equipo',
      message: `¿Eliminar el equipo "${item.name}"?`
    });
    if (!ok) return;
    this.configApi.deleteTeam(item.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.toast.deleted('Equipo', `Se eliminó "${item.name}" correctamente.`); this.cargar(); },
      error: (err) => this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo eliminar el equipo' })
    });
  }

  volverATickets(): void { this.router.navigate(['/tickets']); }
}
